import {
  Card,
  CardHeader,
  SvgIcon,
  CardContent,
  Box,
  Stack,
  Typography,
  Chip,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  IconButton,
  Divider,
  DialogContent,
  Tooltip,
  DialogActions,
} from '@mui/material';
import {
  AppPrimaryButton,
  ConfirmDialog,
} from 'shared/components/AppComponents';
import { ReactComponent as Shield } from 'assets/images/ic_shield_security.svg';
import { ReactComponent as TwoFactorAuthentication } from 'assets/images/ic_2fa_authentication.svg';
import { ReactComponent as SecurityKey } from 'assets/images/ic_key_security.svg';
import { ReactComponent as GoogleAuthenticator } from 'assets/images/ic_google_authenticator.svg';
import { ReactComponent as MicrosoftAuthenticator } from 'assets/images/ic_microsoft_authenticator.svg';
import { SettingService } from 'services/SettingService';
import { Utils } from 'shared/helpers';
import { connect, useSelector } from 'react-redux';
import { AuthActions } from 'state/authentication/Action';
import MailLockIcon from '@mui/icons-material/MailLock';
import { useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OtpInput from 'react-otp-input';
import CloseIcon from '@mui/icons-material/Close';
import { BaseApplicationState } from 'state/baseapplication/Reducer';

const SecuritySettingsCard: React.FC<any> = (props: any) => {
  const theme = useTheme();
  const match = useMediaQuery(theme.breakpoints.down('md'));

  const { setLoginData, authState: twoFactor }: any = props;
  const [isEnablingMFA, setIsEnablingMFA] = useState(false);
  const [isDisablingMFA, setIsDisablingMFA] = useState(false);
  const [showTwoFADialog, setShowTwoFADialog] = useState<any>(null);
  const [copiedToken, setCopiedToken] = useState<any>(null);
  const [totp, setTOTP] = useState<any>(null);
  const [isVerifyingAuth, setIsVerifyingAuth] = useState(false);
  const [isResettingAuth, setIsResettingAuth] = useState(false);
  const [openMFA, setOpenMFA] = useState(false);
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  const handleUpdate2FA = (data: any): void => {
    if (data) {
      setIsEnablingMFA(true);
    }
    if (!data) {
      setIsDisablingMFA(true);
    }
    SettingService.twoFAConfig({
      enabled: data,
    })
      .then(() => {
        setLoginData({
          twoFactor: {
            ...twoFactor,
            isEnabled: data,
          },
        });
        setIsEnablingMFA(false);
        setIsDisablingMFA(false);
      })
      .catch((error) => {
        setIsEnablingMFA(false);
        setIsDisablingMFA(false);
        Utils.handleErrorResponse(error);
      });
  };

  const handleCopy = (tokenId: any): void => {
    navigator.clipboard.writeText(tokenId);
    setCopiedToken(tokenId);
    setTimeout(() => setCopiedToken(null), 500);
  };

  const handleSetupAuthenticator = (): void => {
    SettingService.twoFASetup()
      .then((res) => {
        setShowTwoFADialog(res?.data?.data);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  const handleResetAuthenticator = (): void => {
    setIsResettingAuth(true);
    SettingService.twoFAReset()
      .then(() => {
        setLoginData({
          twoFactor: {
            ...twoFactor,
            enabledMethods: ['email'],
          },
        });
        setIsResettingAuth(false);
      })
      .catch((error) => {
        setIsResettingAuth(false);
        Utils.handleErrorResponse(error);
      });
  };

  const handleVerifyAuthenticator = (data: any): void => {
    setIsVerifyingAuth(true);
    SettingService.verifyTOTP({
      code: data,
    })
      .then(() => {
        setLoginData({
          twoFactor: {
            ...twoFactor,
            enabledMethods: ['email', 'totp'],
          },
        });
        setShowTwoFADialog(null);
        setIsVerifyingAuth(false);
      })
      .catch((error) => {
        setIsVerifyingAuth(false);
        Utils.handleErrorResponse(error);
      });
  };

  const handleCloseMFA = (): void => {
    setOpenMFA(false);
  };

  return (
    <>
      <Card variant="outlined" className="security-settings-content">
        <CardHeader
          title="Security Settings"
          avatar={
            <SvgIcon
              component={Shield}
              inheritViewBox
              className="view-icon"
              fontSize="inherit"
              htmlColor="#76A81F"
            />
          }
        />
        <CardContent>
          <Box className="secure-setting-wrapper">
            <Stack
              direction={match ? 'column' : 'row'}
              className="2FA-auth-content"
            >
              <Box className="secure-header">
                <Stack
                  direction="row"
                  spacing={3}
                  className="secure-textual-status"
                >
                  <SvgIcon
                    component={TwoFactorAuthentication}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                    htmlColor="#76A81F"
                  />
                  <Typography variant="h6">
                    Two-Factor Authentication
                  </Typography>
                  <Chip
                    label={twoFactor?.isEnabled ? 'Enabled' : 'Disabled'}
                    sx={{
                      ...(twoFactor?.isEnabled
                        ? {
                            background: '#06362b !important',
                            color: 'white !important',
                          }
                        : {
                            background: '#ebebeb !important',
                            color: '#7e7e7e !important',
                          }),
                      fontWeight: 600,
                    }}
                  />
                </Stack>
                <Box className="secure-context">
                  <Typography component="span">
                    Add an extra layer of security to your account with TOTP
                    authentication.
                  </Typography>
                </Box>
              </Box>
              {twoFactor?.isEnabled ? (
                <Box className="secure-button">
                  <AppPrimaryButton
                    title="Disable MFA"
                    sx={{
                      background: '#ebebeb !important',
                      color: '#7e7e7e !important',
                    }}
                    onClick={() => {
                      setOpenMFA(true);
                    }}
                    isLoading={isDisablingMFA}
                    disabled={isDisablingMFA}
                    loadingText="Disabling"
                  />
                </Box>
              ) : (
                <Box className="secure-button">
                  <AppPrimaryButton
                    title="Enable MFA"
                    onClick={() => {
                      setOpenMFA(true);
                    }}
                    isLoading={isEnablingMFA}
                    disabled={isEnablingMFA}
                    loadingText="Enabling"
                  />
                </Box>
              )}
            </Stack>

            {twoFactor?.isEnabled && (
              <>
                <Box className="support-auth">
                  <Stack direction="row" spacing={2}>
                    <SvgIcon
                      component={SecurityKey}
                      inheritViewBox
                      className="view-icon"
                      fontSize="inherit"
                      htmlColor="#76A81F"
                    />
                    <Typography variant="h6">
                      Supported Authenticator Apps
                    </Typography>
                  </Stack>
                  <Stack
                    direction={match ? 'column' : 'row'}
                    spacing={2}
                    mb={5}
                  >
                    <Chip
                      icon={
                        <SvgIcon
                          component={GoogleAuthenticator}
                          inheritViewBox
                          className="view-icon"
                          fontSize="inherit"
                          htmlColor="#76A81F"
                        />
                      }
                      label="Google Authenticator"
                      variant="outlined"
                    />

                    <Chip
                      icon={
                        <SvgIcon
                          component={MicrosoftAuthenticator}
                          inheritViewBox
                          className="view-icon"
                          fontSize="inherit"
                          htmlColor="#76A81F"
                        />
                      }
                      label="Microsoft Authenticator"
                      variant="outlined"
                    />
                  </Stack>
                  {orgACLConfig.settings?.includes(
                    'settings.security.setupAuthenticator'
                  ) && (
                    <AppPrimaryButton
                      title={
                        twoFactor?.enabledMethods.includes('totp')
                          ? 'Reset Authenticator'
                          : 'Setup Authenticator'
                      }
                      onClick={() => {
                        if (twoFactor?.enabledMethods.includes('totp')) {
                          handleResetAuthenticator();
                        } else {
                          handleSetupAuthenticator();
                        }
                        setTOTP(null);
                      }}
                      isLoading={isResettingAuth}
                      disabled={isResettingAuth}
                      loadingText="Resetting"
                    />
                  )}
                </Box>

                <Box className="support-auth">
                  <Stack direction="row" spacing={2}>
                    <SvgIcon
                      component={MailLockIcon}
                      inheritViewBox
                      className="view-icon"
                      fontSize="inherit"
                      htmlColor="#76A81F"
                    />
                    <Typography variant="h6">Email</Typography>
                  </Stack>
                  <Typography sx={{ fontSize: '0.8125rem' }} mb={5}>
                    Use your registered email address to get an OTP for 2FA
                  </Typography>

                  {orgACLConfig.settings?.includes(
                    'settings.security.emailConfigured'
                  ) && (
                    <AppPrimaryButton
                      title="Email Configured"
                      onClick={() => handleUpdate2FA(true)}
                      disabled
                    />
                  )}
                </Box>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={showTwoFADialog !== null}
        onClose={() => setShowTwoFADialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            pb: 1,
            pt: 2.5,
            px: 3,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              fontWeight={700}
              fontSize={17}
              lineHeight={1.2}
            >
              Two factor code
            </Typography>
          </Box>
          <IconButton
            onClick={() => setShowTwoFADialog(null)}
            size="small"
            sx={{
              color: '#7e7e7e',
              '&:hover': { backgroundColor: '#f5f5f5' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Typography
            variant="body2"
            color="#7e7e7e"
            sx={{ mb: 2.5, lineHeight: 1.6 }}
          >
            Use <strong>Google Authenticator</strong> to get your code. Without
            this code you cannot login to your account.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box
              sx={{
                p: 1.5,
                border: '2px solid #f0f0f0',
                borderRadius: 3,
                backgroundColor: '#fff',
                boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
              }}
            >
              <img
                src={showTwoFADialog?.url}
                alt="QR Code for 2FA"
                style={{ display: 'block', width: 140, height: 140 }}
              />
            </Box>
          </Box>

          <Typography
            variant="caption"
            fontWeight={600}
            color="#7e7e7e"
            sx={{ mb: 0.75, display: 'block' }}
          >
            Authenticator code:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: '10px 14px',
              backgroundColor: '#f7f8fa',
              border: '1.5px solid #e8e8e8',
              borderRadius: 2.5,
              mb: 2.5,
            }}
          >
            <Typography
              sx={{
                flex: 1,
                fontSize: 11,
                fontWeight: 600,
                wordBreak: 'break-all',
                lineHeight: 1.5,
              }}
            >
              {showTwoFADialog?.secret}
            </Typography>
            <Tooltip
              title="Copied!"
              open={copiedToken === showTwoFADialog?.secret}
            >
              <IconButton onClick={() => handleCopy(showTwoFADialog?.secret)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box mt={5} mb={5}>
            <Typography variant="caption" fontWeight={600} color="#7e7e7e">
              Verification code:
            </Typography>
            <Box
              sx={{ display: 'flex', justifyContent: 'center', mb: 1, mt: 2 }}
            >
              <OtpInput
                value={totp}
                onChange={(value: any) => setTOTP(value)}
                numInputs={6}
                separator={<span className="px-2" />}
                isInputNum
                shouldAutoFocus
                containerStyle="otp-container"
                inputStyle="otp-input-box"
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, gap: 1 }}>
          <AppPrimaryButton
            title="Verify"
            onClick={() => handleVerifyAuthenticator(totp)}
            isLoading={isVerifyingAuth}
            disabled={isVerifyingAuth}
            loadingText="Verifying"
            fullWidth
          />
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        title={twoFactor?.isEnabled ? 'Disable MFA' : 'Enable MFA'}
        subTitle={`Are you sure to ${
          twoFactor?.isEnabled ? 'Disable MFA' : 'Enable MFA'
        }?`}
        showConfirm={openMFA}
        cancelTitle="Close"
        successTitle={twoFactor?.isEnabled ? 'Disable' : 'Enable'}
        onSuccess={() => {
          handleCloseMFA();
          handleUpdate2FA(!twoFactor?.isEnabled);
        }}
        onCancel={handleCloseMFA}
        onCloseDialog={handleCloseMFA}
      />
    </>
  );
};

const mapStateToProps = (state: any): any => ({
  authState: state.authState.twoFactor,
});

const mapDispatchToProps = (dispatch: any): any => ({
  setLoginData: (data: any) => dispatch(AuthActions.setLoginStatus(data)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SecuritySettingsCard);
